import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  $setSelection,
  $getRoot,
  $createRangeSelection,
  FORMAT_ELEMENT_COMMAND,
} from "lexical";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useToolbarState } from "./ToolbarContext";
import BlockFormatDropDown from "./BlockFormatDropDown";

import { merge } from "../../../utils/commonUtils";
import { getSelectedNode } from "../../../utils/lexical";
import { FiBold, FiItalic } from "react-icons/fi";
import { GoStrikethrough } from "react-icons/go";
import { BsLink } from "react-icons/bs";
import {
  AiOutlineAlignCenter,
  AiOutlineAlignLeft,
  AiOutlineAlignRight,
} from "react-icons/ai";
import { TfiAlignJustify } from "react-icons/tfi";
import { IoListOutline } from "react-icons/io5";
import { LiaListOlSolid } from "react-icons/lia";

export const Toolbar = ({ setIsLinkEditMode }) => {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState(new Set());
  const [isLink, setIsLink] = useState(false);
  const [activeListType, setActiveListType] = useState(null);

  const { toolbarState } = useToolbarState();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const formats = new Set();
      if (selection.hasFormat("bold")) formats.add("bold");
      if (selection.hasFormat("italic")) formats.add("italic");
      if (selection.hasFormat("strikethrough")) formats.add("strikethrough");
      setActiveFormats(formats);
      const node = getSelectedNode(selection);
      const parent = node.getParent();

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatList = (type) => {
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return;
      }

      const anchorNode = selection.anchor.getNode();
      const focusNode = selection.focus.getNode();
      const root = $getRoot();

      // Toggle list type - if clicking the same type again, deactivate it
      const newListType = activeListType === type ? null : type;
      setActiveListType(newListType);

      if (anchorNode === root || focusNode === root) {
        // If either anchor or focus is at the root, create a paragraph node first
        const paragraph = $createParagraphNode();

        root.append(paragraph);

        // Set selection to the new paragraph
        const rangeSelection = $createRangeSelection();

        rangeSelection.anchor.set(paragraph.getKey(), 0, "element");
        rangeSelection.focus.set(paragraph.getKey(), 0, "element");
        $setSelection(rangeSelection);
      }

      // If we're toggling off the active list, convert to paragraph
      if (!newListType) {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
        return;
      }

      // Apply the list type
      editor.dispatchCommand(
        newListType === "ul"
          ? INSERT_UNORDERED_LIST_COMMAND
          : INSERT_ORDERED_LIST_COMMAND,
        undefined
      );
    });
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      setIsLinkEditMode(false);

      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink, setIsLinkEditMode]);

  const ELEMENT_FORMAT_OPTIONS = {
    left: {
      name: "Left Align",
      icon: <AiOutlineAlignLeft className="text-sm md:text-base" />,
    },
    center: {
      name: "Center Align",
      icon: <AiOutlineAlignCenter className="text-sm md:text-base" />,
    },
    right: {
      name: "Right Align",
      icon: <AiOutlineAlignRight className="text-sm md:text-base" />,
    },
    justify: {
      name: "Justify Align",
      icon: <TfiAlignJustify className="text-sm md:text-base" />,
    },

  };

  const handleCommand = (cmd, val) => {
    editor.dispatchCommand(cmd, val);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <BlockFormatDropDown
          editor={editor}
          blockType={toolbarState.blockType}
          open={open}
          setOpen={setOpen}
          dropdownRef={dropdownRef}
        />
        {/* <div className="w-px h-6 bg-gray-300 block" />

        <FontSize editor={editor} /> */}

        <div className="w-px h-6 bg-gray-300 block" />
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            activeFormats.has("bold") && "bg-gray-200"
          )}
          onClick={() => formatText("bold")}
        >
          <FiBold className="text-sm md:text-base" />
        </button>
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            activeFormats.has("italic") && "bg-gray-200"
          )}
          onClick={() => formatText("italic")}
        >
          <FiItalic className="text-sm md:text-base" />
        </button>
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            activeFormats.has("strikethrough") && "bg-gray-200"
          )}
          onClick={() => formatText("strikethrough")}
        >
          <GoStrikethrough className="text-sm md:text-base" />
        </button>
        <div className="w-px h-6 bg-gray-300 block" />
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            activeListType === "ul" && "bg-gray-200"
          )}
          onClick={() => formatList("ul")}
          title="Bullet List"
        >
          <IoListOutline className="text-sm md:text-base" />
        </button>
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            activeListType === "ol" && "bg-gray-200"
          )}
          onClick={() => formatList("ol")}
          title="Numbered List"
        >
          <LiaListOlSolid className="text-sm md:text-base" />
        </button>
        <div className="w-px h-6 bg-gray-300 block" />
        <button
          type="button"
          className={merge(
            "text-sm font-normal w-7 md:w-9 h-7 md:h-8 px-1.5 md:px-2 !py-1.5 border border-gray-200 rounded",
            isLink && "bg-gray-200"
          )}
          onClick={insertLink}
          title={isLink ? "Remove Link" : "Add Link"}
        >
          <BsLink className={`text-sm md:text-base `} />
        </button>
        <div className="w-px h-6 bg-gray-300 block" />
        <div className="inline-flex rounded-md shadow-sm" role="group">
          {Object.entries(ELEMENT_FORMAT_OPTIONS).map(
            ([key, { name, icon }]) => (
              <button
                key={key}
                type="button"
                className={merge(
                  "px-1.5 md:px-2 py-1.5 md:py-2 w-7 md:w-9 h-7 md:h-8 text-sm font-medium border border-gray-200",
                  "hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  "first:rounded-l-lg last:rounded-r-lg"
                )}
                onClick={() => handleCommand(FORMAT_ELEMENT_COMMAND, key)}
                title={name}
              >
                {icon}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};
